<script lang="ts" setup>
import SkrButton from '@/components/button/SkrButton.vue'

const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()
const { lgAndUp } = useDisplay()
const { track, setSignupContext } = useAnalytics()

const FISHEYE_FILTER_ID = 'nas-promo-fisheye'

const panelRef = useTemplateRef<HTMLElement>('panel')
const feImageRef = useTemplateRef<SVGFEImageElement>('feImage')
const paperHeroRef = useTemplateRef<HTMLImageElement>('paperHero')
const paperTwoRef = useTemplateRef<HTMLImageElement>('paperTwo')
const paperThreeRef = useTemplateRef<HTMLImageElement>('paperThree')
const paperFourRef = useTemplateRef<HTMLImageElement>('paperFour')
const paperFiveRef = useTemplateRef<HTMLImageElement>('paperFive')

const effectsEnabled = computed(() => lgAndUp.value)

useDotFieldReveal(panelRef, feImageRef, effectsEnabled)
usePaperParallax(
  panelRef,
  () => [
    { el: paperHeroRef.value, factor: 1 },
    { el: paperTwoRef.value, factor: 0.5 },
    { el: paperThreeRef.value, factor: 0.5 },
    { el: paperFourRef.value, factor: 0.5 },
    { el: paperFiveRef.value, factor: 0.5 },
  ],
  effectsEnabled
)

const goToSignupNas = async () => {
  setSignupContext({ source: 'nas-conversion', plan: 'starter' })
  track(ANALYTICS_EVENTS.SIGNUP_STARTED)

  await navigateTo({ name: 'signup-starter' })
  emit('close')
}
</script>

<template>
  <div ref="panel" class="promo" :style="{ '--fisheye-filter': `url(#${FISHEYE_FILTER_ID})` }">
    <div v-if="lgAndUp" class="promo__papers">
      <img ref="paperHero" class="promo__paper promo__paper--hero" src="/img/promo-paper-signed.png" alt="" />
      <img ref="paperTwo" class="promo__paper promo__paper--2" src="/img/promo-paper4.png" alt="" />
      <img ref="paperThree" class="promo__paper promo__paper--3" src="/img/promo-paper3.png" alt="" />
      <img ref="paperFour" class="promo__paper promo__paper--4" src="/img/promo-paper2.png" alt="" />
      <img ref="paperFive" class="promo__paper promo__paper--5" src="/img/promo-paper5.png" alt="" />
    </div>

    <h2 class="promo__title">{{ t('sign.success.promo.title') }}</h2>
    <p class="promo__quote">{{ t('sign.success.promo.quote') }}</p>
    <p class="promo__body">
      <span class="promo__body-hl">{{ t('sign.success.promo.body_hl') }}</span>
      <i18n-t class="promo__body-line2" keypath="sign.success.promo.body" tag="span">
        <template #strong>
          <strong>{{ t('sign.success.promo.body_strong') }}</strong>
        </template>
      </i18n-t>
    </p>

    <skr-button
      class="promo__cta"
      color="purple-dark"
      :ripple="false"
      data-cy="upgrade_to_starter_plan_button"
      @click="goToSignupNas"
    >
      <span class="promo__cta-content">
        <span>{{ t('sign.success.promo.cta') }}</span>
        <v-icon size="32">custom:arrow_long</v-icon>
      </span>
    </skr-button>

    <svg v-if="lgAndUp" width="0" height="0" class="promo__filter-defs" aria-hidden="true">
      <filter :id="FISHEYE_FILTER_ID" color-interpolation-filters="sRGB" x="-15%" y="-15%" width="130%" height="130%">
        <feFlood flood-color="rgb(128,128,128)" result="mid" />
        <feImage ref="feImage" result="bump" x="-2000" y="-2000" width="300" height="300" preserveAspectRatio="none" />
        <feComposite in="bump" in2="mid" operator="over" result="dmap" />
        <feDisplacementMap in="SourceGraphic" in2="dmap" scale="2.7" xChannelSelector="R" yChannelSelector="G" />
      </filter>
    </svg>
  </div>
</template>

<style lang="scss" scoped>
.promo {
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  padding: 8 * $bw 6 * $bw;
  border-radius: 4 * $bw;
  background: linear-gradient(180deg, $c-promo-bg-start 0%, $c-promo-bg-end 100%);
  color: $c-skribbleu;

  @include min-width(lg) {
    justify-content: center;
    overflow-x: hidden;
    overflow-y: auto;
    height: 100%;
    padding: clamp(8 * $bw, 7vh, 16 * $bw) 18 * $bw;
    border-radius: 8 * $bw;
    text-align: center;
  }

  // Cursor-revealed dot texture: bulged by the fisheye filter under the
  // pointer and shown only through the --trail mask that useDotFieldReveal
  // rebuilds each frame — invisible until hovered.
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    pointer-events: none;
    background-image: url('/img/promo-dot-field.svg');
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    opacity: 0.9;
    filter: var(--fisheye-filter, none);
    -webkit-mask-image: var(--trail, linear-gradient(transparent, transparent));
    mask-image: var(--trail, linear-gradient(transparent, transparent));
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
  }

  &__papers {
    position: relative;
    flex-shrink: 0;
    width: 220px;
    margin: 0 auto;
  }

  &__paper {
    z-index: 0;
    pointer-events: none;
    transition: transform 0.35s cubic-bezier(0.32, 0.71, 0, 0.98);

    &--hero {
      display: block;
      width: 220px;
      height: 162px;
    }
    &--2 {
      position: absolute;
      width: 110px;
      height: 97px;
      top: 4px;
      right: 100%;
      margin-right: -30px;
    }
    &--3 {
      position: absolute;
      width: 118px;
      height: 80px;
      top: 82px;
      right: 100%;
      margin-right: 60px;
    }
    &--4 {
      position: absolute;
      width: 103px;
      height: 72px;
      top: 90px;
      left: 100%;
      margin-left: 12px;
    }
    &--5 {
      position: absolute;
      width: 88px;
      height: 76px;
      top: 9.7px;
      left: 100%;
      margin-left: 50px;
    }
  }

  &__title {
    position: relative;
    z-index: 1;
    margin: 0 auto;
    width: 100%;
    max-width: 450px;
    font-size: 1.125rem;
    font-weight: 800;
    line-height: 1.3;
    letter-spacing: -0.2px;

    @include min-width(lg) {
      margin: clamp(4 * $bw, 4vh, 8 * $bw) 0 0;
      width: auto;
      max-width: none;
      font-size: clamp(22px, 3.2vh, 28px);
    }
  }

  &__quote {
    position: relative;
    z-index: 1;
    margin: 3 * $bw auto 0;
    width: 100%;
    max-width: 450px;
    font-size: 1.125rem;
    line-height: 1.6;
    letter-spacing: -0.1px;

    @include min-width(lg) {
      margin-top: clamp(3 * $bw, 3vh, 5 * $bw);
      width: auto;
      max-width: 490px;
      font-size: clamp(17px, 2.4vh, 20px);
    }
  }

  &__body {
    position: relative;
    z-index: 1;
    margin: 6 * $bw auto 0;
    width: 100%;
    max-width: 450px;
    font-size: 0.9375rem;
    line-height: 1.6;

    strong {
      font-weight: 800;
    }

    @include min-width(lg) {
      margin: clamp(7 * $bw, 9vh, 20 * $bw) 0 0;
      width: auto;
      max-width: none;
      font-size: clamp(0.9375rem, 2.2vh, 1.125rem);
    }
  }

  &__body-hl {
    display: block;
    text-decoration: underline;
  }

  &__body-line2 {
    display: block;
    margin-top: 3 * $bw;
  }

  &__cta {
    position: relative;
    z-index: 1;
    flex-shrink: 0;
    margin: 7 * $bw auto 0;
    width: 100%;
    max-width: 450px;
    border-radius: 34px;
    box-shadow: 0 16px 24px rgba(40, 62, 102, 0.1) !important;
    transition: width 0.3s cubic-bezier(0.32, 0.71, 0, 0.98);

    @include min-width(lg) {
      margin-top: clamp(4 * $bw, 4vh, 10 * $bw);
      width: 300px;
      max-width: none;

      &:hover {
        width: 330px;
      }
    }

    &.v-btn {
      justify-content: stretch;
    }

    &.v-btn:not(.v-btn--round).v-btn--size-default {
      height: 13 * $bw;
      padding: 0 (7 * $bw);

      @include min-width(lg) {
        height: 17 * $bw;
      }
    }

    :deep(.v-btn__content) {
      width: 100%;
      font-size: 1.125rem;
      font-weight: 800;
      color: $c-white;
    }

    :deep(.v-btn__overlay) {
      opacity: 0;
    }
  }

  &__cta-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  &__filter-defs {
    position: absolute;
  }
}
</style>
